import React from "react";
import {
  AppButton, AppCard, AppCardContent, AppCardHeader, AppCardTitle, Callout,
} from "../../ui";
import QuestionPreview from "./QuestionPreview";
import {
  DIFFICULTIES, STEPS, answerSummary, marksSummary, typeLabel,
} from "./questionRules";

const stepTitle = (key) => STEPS.find((s) => s.key === key)?.title || key;

/** One line of the summary, with the way back to the step that sets it. */
function Row({ label, value, step, onGoTo }) {
  return (
    <div className="tw-flex tw-items-start tw-gap-3 tw-py-2.5 tw-border-0 tw-border-t tw-border-solid tw-border-line first:tw-border-t-0">
      <dt className="tw-w-28 tw-shrink-0 tw-text-[0.8125rem] tw-font-normal tw-text-content-muted">{label}</dt>
      <dd className="tw-m-0 tw-min-w-0 tw-flex-1 tw-text-[0.8125rem] tw-text-content">{value}</dd>
      <AppButton type="button" variant="ghost" size="xs" onClick={() => onGoTo(step)} aria-label={`Change ${label.toLowerCase()}`}>
        Change
      </AppButton>
    </div>
  );
}

/**
 * Step 6 — the question once more, as students will see it, and every choice
 * behind it with a way back to change it. Anything still missing is listed
 * first, each with the step that fixes it.
 */
export default function ReviewStep({ form, categories, passage, problems, onGoTo, isLibrary }) {
  const cat = categories.find((c) => String(c._id) === String(form.category));
  const sub = categories.find((c) => String(c._id) === String(form.subcategory));
  const difficulty = DIFFICULTIES.find((d) => d.value === form.difficulty)?.label || form.difficulty;
  const extras = [
    String(form.explanation || "").trim() && "explanation",
    String(form.hint || "").trim() && "hint",
  ].filter(Boolean);

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      {problems.length > 0 ? (
        <Callout tone="warning" role="alert">
          <span className="tw-flex tw-flex-col tw-gap-1.5">
            <span className="tw-font-semibold">Before saving:</span>
            {problems.map((p) => (
              <span key={`${p.step}-${p.message}`} className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2">
                {p.message}
                <AppButton type="button" variant="ghost" size="xs" onClick={() => onGoTo(p.step)}>
                  Go to {stepTitle(p.step)}
                </AppButton>
              </span>
            ))}
          </span>
        </Callout>
      ) : (
        <Callout tone="success">Everything is in place. Save it to the bank — any assessment can use it.</Callout>
      )}

      <AppCard>
        <AppCardHeader>
          <AppCardTitle>How students see it</AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          <QuestionPreview form={form} passage={passage} />
        </AppCardContent>
      </AppCard>

      <AppCard>
        <AppCardHeader>
          <AppCardTitle>Details</AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          <dl className="tw-m-0">
            <Row
              label="Topic"
              step="topic"
              onGoTo={onGoTo}
              value={cat ? `${cat.name}${sub ? ` › ${sub.name}` : ""}` : "Not chosen yet"}
            />
            {isLibrary && (
              <Row
                label="Marketplace"
                step="topic"
                onGoTo={onGoTo}
                value={form.isPublic ? "Available when its folder is published" : "Held back"}
              />
            )}
            <Row label="Type" step="type" onGoTo={onGoTo} value={typeLabel(form.questionType)} />
            <Row label="Answer" step="answer" onGoTo={onGoTo} value={answerSummary(form)} />
            <Row label="Marks" step="marks" onGoTo={onGoTo} value={marksSummary(form)} />
            <Row
              label="Difficulty"
              step="marks"
              onGoTo={onGoTo}
              value={`${difficulty} · about ${form.estimatedTime} seconds`}
            />
            <Row
              label="Notes"
              step="marks"
              onGoTo={onGoTo}
              value={[
                extras.length ? `With ${extras.join(" and ")}` : "No explanation or hint",
                form.tags.length ? `tags: ${form.tags.join(", ")}` : "",
                form.source ? `source: ${form.source}` : "",
              ].filter(Boolean).join(" · ")}
            />
            {form.passage && (
              <Row label="Passage" step="question" onGoTo={onGoTo} value={passage?.title || "Attached"} />
            )}
          </dl>
        </AppCardContent>
      </AppCard>
    </div>
  );
}
